/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, generatePath } from 'react-router-dom';

import { routes as routeConstants } from 'src/constants';
import { withLoader } from 'src/core/components';
import { PlateWell } from 'src/models';
import { selectDetailsData } from 'src/core/redux/selectors';
import {
  PRIMER_NAME,
  OLIGO_SEQUENCE,
  MODIFICATION_3,
  MODIFICATION_5,
} from 'src/constants/forms';

import {
  fetchPlateWellDetails as fetchPlateWellDetailsAction,
  updatePlateWell as updatePlateWellAction
} from './actions';
import { getFormValidators } from './validators';
import EditSequenceComponent from './EditSequenceComponent';

const getFormState = (plateWell = {}) => ({
  [PRIMER_NAME]: {
    value: plateWell[PRIMER_NAME] || '',
    isValid: !!plateWell[PRIMER_NAME],
  },
  [OLIGO_SEQUENCE]: {
    value: plateWell[OLIGO_SEQUENCE] || '',
    isValid: !!plateWell[OLIGO_SEQUENCE],
  },
  [MODIFICATION_5]: {
    value: plateWell[MODIFICATION_5] || '',
    isValid: true,
  },
  [MODIFICATION_3]: {
    value: plateWell[MODIFICATION_3],
    isValid: true,
  },
});

const EditSequenceContainer = ({
  plateWellDetails,
  updatePlateWell,
  history,
  plateName,
  showLoader,
  hideLoader,
  fetchPlateWellDetails,
  location
}) => {
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [wellsError, setWellsError] = React.useState('');
  const [formState, setFormState] = React.useState(getFormState(plateWellDetails));
  const [enableActionButtons, setEnableActionButtons] = React.useState(false);

  const onChangeField = (value, fieldName, isValid) => {
    setWellsError('');
    setFormState((prevState) => ({
      ...prevState,
      [fieldName]: {
        ...prevState[fieldName],
        value: value || '',
        isValid,
      }
    }));

    if (plateWellDetails[fieldName] !== value) {
      setEnableActionButtons(true);
    } else {
      setEnableActionButtons(false);
    }
  };

  const goBack = () => {
    const newPath = generatePath(routeConstants.EDIT_PLATE_PAGE, { plateName });

    setEnableActionButtons(false);
    history.replace(newPath);
  };

  const onSubmit = async () => {
    setEnableActionButtons(false);
    showLoader();
    await updatePlateWell(formState,
      plateName, location.search, setWellsError, setEnableActionButtons);
    hideLoader();
  };

  const onPreview = () => {};

  React.useEffect(() => {
    setFormState(getFormState(plateWellDetails));
  }, [
    plateWellDetails[PRIMER_NAME],
    plateWellDetails[OLIGO_SEQUENCE],
    plateWellDetails[MODIFICATION_5],
    plateWellDetails[MODIFICATION_3]
  ]);

  React.useEffect(() => {
    setWellsError('');
  }, []);

  React.useEffect(() => {
    fetchPlateWellDetails(plateName, location.search, setWellsError, setIsInitialLoad);
  }, [plateName, location.search, setWellsError]);

  return (
    <EditSequenceComponent
      {...{
        onChangeField,
        formState,
        onCancel: goBack,
        onBackClick: goBack,
        onSubmit,
        canSubmit: PlateWell.isDataValid(formState) && !wellsError,
        onPreview,
        validators: getFormValidators(),
        plateName,
        error: wellsError,
        clearError: () => setWellsError(''),
        isInitialLoad, // errors show briefly in input on first load so we don't render form
        // just for the initial load
        enableActionButtons
      }}
    />
  );
};

EditSequenceContainer.propTypes = {
  plateWellDetails: PropTypes.object,
  updatePlateWell: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  plateName: PropTypes.string.isRequired,
  showLoader: PropTypes.func.isRequired,
  hideLoader: PropTypes.func.isRequired,
  fetchPlateWellDetails: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  plateSize: PropTypes.number
};

const makeMapStateToProps = () => {
  const plateWellDetailsSelector = selectDetailsData(PlateWell);

  const mapStateToProps = (state, ownProps) => {
    const plateName = ownProps.match?.params?.plateName;

    return {
      plateWellDetails: plateWellDetailsSelector(state),
      plateName
    };
  };

  return mapStateToProps;
};

const mapDispatchToProps = ({
  fetchPlateWellDetails: fetchPlateWellDetailsAction,
  updatePlateWell: updatePlateWellAction,
});

export default compose(
  withRouter,
  withLoader,
  connect(makeMapStateToProps, mapDispatchToProps)
)(EditSequenceContainer);
